import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/utils/api';
import { setAuth } from '@/utils/auth';
import { PLAN_OPTIONS } from '@/components/pricing/pricingData';
import { emitSignupFunnelEvent } from '@/utils/signupFunnel';
import ProgressBar from '@/components/signup/ProgressBar';
import AccountTypeStep from '@/components/signup/AccountTypeStep';
import PaymentOptionStep from '@/components/signup/PaymentOptionStep';
import DetailsStep from '@/components/signup/DetailsStep';
import CredentialsStep from '@/components/signup/CredentialsStep';
import NotificationsStep from '@/components/signup/NotificationsStep';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@/components/signup/NotificationPreferencesSection';
import useFieldErrors from '@/components/signup/useFieldErrors';
import {
  validateDetails, validateCredentials, buildSignupPayload, warnEmailDelivery, parseSignupError, startOrgCheckout,
} from '@/components/signup/signupForm';

const STEPS = { ACCOUNT_TYPE: 1, PAYMENT_OPTION: 2, DETAILS: 3, CREDENTIALS: 4, NOTIFICATIONS: 5 };

const STEP_NAME_BY_ID = {
  [STEPS.ACCOUNT_TYPE]: 'account_type',
  [STEPS.PAYMENT_OPTION]: 'payment_option',
  [STEPS.DETAILS]: 'details',
  [STEPS.CREDENTIALS]: 'credentials',
};

const BACK_STEP = {
  [STEPS.PAYMENT_OPTION]: () => STEPS.ACCOUNT_TYPE,
  [STEPS.DETAILS]: (isOrg) => (isOrg ? STEPS.PAYMENT_OPTION : STEPS.ACCOUNT_TYPE),
  [STEPS.CREDENTIALS]: () => STEPS.DETAILS,
  [STEPS.NOTIFICATIONS]: () => STEPS.CREDENTIALS,
};

const INITIAL_FORM = {
  account_type: '', selected_tier: '', units: 1, additional_users: 0, payment_option: '',
  company_name: '', full_name: '', email: '', password: '', confirmPassword: '',
  contact_number: '', referral_code: '',
};

const funnelMeta = (f) => ({ account_type: f.account_type, selected_tier: f.selected_tier, payment_option: f.payment_option });

const returnUrlFor = (f) => {
  if (f.account_type !== 'organization') return '/dashboard';
  return f.payment_option === 'pay_now' ? '/billing' : '/onboarding';
};

export default function Signup() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(STEPS.ACCOUNT_TYPE);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATION_PREFERENCES);
  const fieldErrors = useFieldErrors();
  const navigate = useNavigate();
  const isOrg = formData.account_type === 'organization';
  const detailsErrors = validateDetails(formData);
  const credentialsErrors = validateCredentials(formData);

  useEffect(() => {
    const plan = searchParams.get('plan');
    const refCode = searchParams.get('ref');
    if (plan === 'freelancer' || plan === 'organization') {
      setFormData((prev) => ({ ...prev, account_type: plan, selected_tier: plan }));
      setStep(plan === 'freelancer' ? STEPS.DETAILS : STEPS.PAYMENT_OPTION);
    }
    if (refCode) {
      setFormData((prev) => ({ ...prev, referral_code: refCode }));
      toast.success('Referral code applied!');
    }
  }, [searchParams]);

  useEffect(() => {
    const name = STEP_NAME_BY_ID[step];
    if (name) emitSignupFunnelEvent(name, 'viewed');
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    fieldErrors.clearServerError(name);
  };

  // Marks the step attempted; returns true when it has no errors.
  const stepIsValid = (stepKey, errors) => {
    fieldErrors.markAttempted(stepKey);
    const ok = Object.keys(errors).length === 0;
    if (!ok) emitSignupFunnelEvent('error', 'viewed', { error_code: `${stepKey}_incomplete` });
    return ok;
  };

  const selectAccountType = (type) => {
    emitSignupFunnelEvent('account_type', 'completed', { account_type: type });
    setFormData((prev) => ({ ...prev, account_type: type, selected_tier: type }));
    setStep(type === 'freelancer' ? STEPS.DETAILS : STEPS.PAYMENT_OPTION);
  };

  const selectPayment = (choice) => {
    emitSignupFunnelEvent('payment_option', 'completed', { payment_option: choice, selected_tier: formData.selected_tier });
    setFormData((prev) => ({ ...prev, payment_option: choice }));
    setStep(STEPS.DETAILS);
  };

  const handleDetailsNext = () => {
    if (!stepIsValid('details', detailsErrors)) return;
    emitSignupFunnelEvent('details', 'completed', { account_type: formData.account_type });
    setStep(STEPS.CREDENTIALS);
  };

  const handleCredentialsNext = (e) => {
    e?.preventDefault?.();
    if (!stepIsValid('credentials', credentialsErrors)) return;
    emitSignupFunnelEvent('credentials', 'completed', { account_type: formData.account_type });
    setStep(STEPS.NOTIFICATIONS);
  };

  const completeSignup = async (data) => {
    toast.success('Account created — welcome to SAFER-i!');
    warnEmailDelivery(data.signup?.email_send_status);
    emitSignupFunnelEvent('success', 'viewed', funnelMeta(formData));
    if (data.requires_totp_enrollment || data.requires_totp) {
      sessionStorage.setItem('safer_mfa_token', data.mfa_token);
      sessionStorage.setItem('safer_mfa_mode', data.requires_totp_enrollment ? 'enroll' : 'challenge');
      sessionStorage.setItem('safer_return_url', returnUrlFor(formData));
      navigate('/two-factor');
      return;
    }
    if (data.access_token) setAuth(data.access_token, data.user || {});
    if (isOrg && formData.payment_option === 'pay_now') {
      const fallback = await startOrgCheckout(formData);
      if (fallback) navigate(fallback);
      return;
    }
    navigate(returnUrlFor(formData));
  };

  const handleSignup = async (e) => {
    e?.preventDefault?.();
    if (!stepIsValid('credentials', credentialsErrors)) { setStep(STEPS.CREDENTIALS); return; }
    emitSignupFunnelEvent('credentials', 'completed', { account_type: formData.account_type });
    emitSignupFunnelEvent('submit', 'viewed', funnelMeta(formData));
    setLoading(true);
    try {
      const { data = {} } = await api.post('/auth/signup', buildSignupPayload(formData, notifications), { skipErrorToast: true });
      await completeSignup(data);
    } catch (error) {
      const { field, message } = parseSignupError(error);
      if (field) {
        fieldErrors.setServerError(field, message);
        setStep(STEPS.CREDENTIALS);
      } else {
        toast.error(message);
      }
      emitSignupFunnelEvent('error', 'viewed', { error_code: String(error.response?.status || 'network') });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    const prev = BACK_STEP[step];
    if (prev) setStep(prev(isOrg));
  };

  const selectedTierObj = isOrg ? PLAN_OPTIONS.organization : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="signup-home-link">
            <img src="/safer-i-logo.png" alt="SAFER-i" className="w-12 h-12 rounded-xl" />
            <span className="text-2xl font-bold">SAFER-i™</span>
          </Link>
        </div>

        <Card className="shadow-xl border-0">
          <ProgressBar step={step} isFreelancer={formData.account_type === 'freelancer'} />

          {step === STEPS.ACCOUNT_TYPE && <AccountTypeStep onSelect={selectAccountType} />}

          {step === STEPS.PAYMENT_OPTION && (
            <PaymentOptionStep tier={selectedTierObj} units={formData.units} selected={formData.payment_option}
              onBack={goBack} onSelect={selectPayment} />
          )}

          {step === STEPS.DETAILS && (
            <DetailsStep accountType={formData.account_type} tier={selectedTierObj} formData={formData}
              errors={fieldErrors.visibleErrors(detailsErrors, 'details')}
              onChange={handleChange} onBlur={fieldErrors.handleBlur} onBack={goBack} onNext={handleDetailsNext} />
          )}

          {step === STEPS.CREDENTIALS && (
            <CredentialsStep accountType={formData.account_type} tier={selectedTierObj} units={formData.units}
              formData={formData} errors={fieldErrors.visibleErrors(credentialsErrors, 'credentials')}
              showPassword={showPassword} loading={loading} onChange={handleChange} onBlur={fieldErrors.handleBlur}
              onToggleShowPassword={() => setShowPassword(!showPassword)} onBack={goBack}
              onSubmit={isOrg ? handleCredentialsNext : handleSignup} submitLabel={isOrg ? 'Continue' : undefined} />
          )}

          {step === STEPS.NOTIFICATIONS && (
            <NotificationsStep value={notifications} onChange={setNotifications} loading={loading}
              onBack={goBack} onSubmit={handleSignup} />
          )}
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Need help? <a href="mailto:support@safer-i.com" className="text-primary hover:underline" data-testid="signup-support-link">Contact Support</a>
        </p>
      </div>
    </div>
  );
}
